import { Component, useState } from "react";
import styles from "./cart.module.less";
import CartItem from "components/block-elements/cart-item/cart-item";
import { GlobalContext } from "components/stores/global-context";
import {
  ECommerceHelper,
  EventAggregator,
  ProductStore,
  CartContextConsumer,
  extract,
  OutlineButton,
  OutlineButtonLink,
  PrimaryButtonLink,
} from "@parksandresorts/core";
import SiteConfigurationStore from "components/stores/site-configuration-store";

const ContinueShoppingButton = ({ continueShoppingLink, continueShoppingLabel, context }) => {
  const slug = continueShoppingLink?.internalLink?.slug;
  const continueLabel = continueShoppingLink?.title ?? continueShoppingLabel;

  return (
    <span
      onClick={() =>
        context.setValue({
          showCart: false,
          showMenu: false,
          showSearch: false,
          isMenuAnimating: true,
          isMenuAnimationDone: false,
          isMainVisible: true,
        })
      }
    >
      {slug ? (
        <OutlineButtonLink label={continueLabel} url={slug} isFullWidth />
      ) : (
        <OutlineButton label={continueShoppingLabel} isFullWidth />
      )}
    </span>
  );
};

const BlackWeekOnly = ({ totalSumInclVat }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  function getQuantityText(totalSumInclVat) {
    return `${Math.floor(totalSumInclVat / 450)} st`;
  }
  return (
    <>
      <div className={styles.bwWrapper}>
        <img className={styles.bwImg} alt="Text Black week" src="/images/black-week/GLT_BW_BlackWeek.png" />
        <div className={styles.bwInnerWrapper}>
          <p data-contentful-label-id="BW_CART_TEXT" className={styles.bwText}>
            {SiteConfigurationStore.getLabel("BW_CART_TEXT", "Antal gratis åkband som ingår i ditt köp")}
          </p>
          <span className={styles.bwQuantity}>{getQuantityText(totalSumInclVat)}</span>
        </div>
        <button
          className={styles.bwReadMore}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="readMoreContent"
          data-contentful-label-id="BW_CART_READ_MORE_LABEL & BW_CART_READ_LESS_LABEL"
        >
          {isExpanded
            ? SiteConfigurationStore.getLabel("BW_CART_READ_LESS_LABEL", "Läs mindre")
            : SiteConfigurationStore.getLabel("BW_CART_READ_MORE_LABEL", "Varför får jag gratis åkband?")}
        </button>
        <div
          data-contentful-label-id="BW_CART_READ_MORE_TEXT"
          id="readMoreContent"
          style={{ display: isExpanded ? "block" : "none" }}
          aria-hidden={!isExpanded}
        >
          {SiteConfigurationStore.getLabel(
            "BW_CART_READ_MORE_TEXT",
            "För varje 450 kronor du handlar för får du automatiskt ett åkband med på köpet! Gäller hela Black week till och med 29/11.",
          )}
        </div>
      </div>
    </>
  );
};

export default class Cart extends Component {
  static defaultProps = {
    hideContinueShopping: false,
  };

  constructor(props) {
    super(props);
    this.cartLabel = extract(ECommerceHelper, "jetshop.cartLabel");
    this.cartSummeryLabel = extract(ECommerceHelper, "jetshop.cartSummeryLabel");
    this.toCheckoutLabel = extract(ECommerceHelper, "jetshop.jetshopCheckoutUrl.title");
    this.continueShoppingLabel = extract(ECommerceHelper, "jetshop.continueShoppingLabel");
    this.continueShoppingLink = extract(ECommerceHelper, "jetshop.continueShoppingLink");
    this.toCheckoutUrl = extract(ECommerceHelper, "jetshop.jetshopCheckoutUrl.externalUrl");
    this.currencyDisplayName = extract(ECommerceHelper, "jetshop.currencyDisplayName");
    this.articleLabel = extract(ECommerceHelper, "jetshop.cartArticleLabel");
    this.articlesLabel = extract(ECommerceHelper, "jetshop.cartArticlesLabel");
    this.cartIsEmptyLabel = extract(ECommerceHelper, "jetshop.cartIsEmptyLabel");
    this.upsellPageUrl = extract(ECommerceHelper, "jetshop.upsellPageUrl");
    const hideContinueShopping = extract(props, "hideContinueShopping");
    this.showContinueShopping = !hideContinueShopping;
  }

  getCartLabel(itemsInCart) {
    if (itemsInCart && itemsInCart > 0) {
      return `${this.cartLabel} (${itemsInCart} ${itemsInCart > 1 ? this.articlesLabel : this.articleLabel})`;
    }
    return `${this.cartLabel} ${this.cartIsEmptyLabel}`;
  }

  isCartUpsellable(cartContext) {
    return (
      !cartContext.isUpsellVisited &&
      cartContext.upsellItems &&
      cartContext.upsellItems.length > 0 &&
      this.upsellPageUrl
    );
  }

  getCheckoutUrl(cartContext) {
    if (this.isCartUpsellable(cartContext)) {
      return this.upsellPageUrl;
    }
    return cartContext.getCheckoutUrl();
  }

  trackGoToCheckoutEvent(cartContext) {
    if (this.isCartUpsellable(cartContext)) {
      EventAggregator.publish("trackEvent", { category: "Cart", action: "Clicked button", label: "Upsale" });
    } else {
      const products = cartContext.products.map((product) => {
        const contentfulProduct = ProductStore.getProductByJetshopId(product.jetshopId);
        return {
          name: product.name,
          id: product.jetshopId,
          price: product.price,
          category:
            contentfulProduct && contentfulProduct.tags instanceof Array && contentfulProduct.tags.length
              ? contentfulProduct.tags[0].title
              : undefined,
          quantity: 1,
          glkId:
            contentfulProduct && contentfulProduct.productData.glkId
              ? parseInt(contentfulProduct.productData.glkId)
              : null,
        };
      });

      EventAggregator.publish("trackEvent", { category: "Cart", action: "Clicked button", label: "Checkout" });

      EventAggregator.publish("trackEcommerce", {
        event: "checkout",
        action: {
          checkout: {
            actionField: { step: 1 },
            products: products,
          },
        },
      });
    }
  }

  render() {
    return (
      <div className={styles.cart}>
        <CartContextConsumer>
          {(cartContext) => (
            <div className={styles.cart__fullView}>
              <div className={[styles.cart__title, "h4"].join(" ")}>{this.getCartLabel(cartContext.totalItems)}</div>
              {cartContext && (
                <ul className={styles.cart__list}>
                  {cartContext.products &&
                    cartContext.products.map((product) => {
                      return (
                        <li key={product.jetshopCartItemId} className={styles.cart__listItem}>
                          <CartItem {...product} cartContext={cartContext} />
                        </li>
                      );
                    })}
                </ul>
              )}
              {SiteConfigurationStore.siteConfiguration.enableBlackWeekOffer && (
                <BlackWeekOnly totalSumInclVat={cartContext.totalSumInclVat} />
              )}
              <div className={styles.cart__checkoutWrapper}>
                <div className={styles.cart__sum}>
                  <span className={styles.cart__sumLabel}>{this.cartSummeryLabel}</span>
                  <span className={styles.cart__sumAmount}>{`${Math.round(cartContext.totalSumInclVat)} ${
                    this.currencyDisplayName
                  }`}</span>
                </div>
                <div className={styles.cart__bottomBtnWrapper}>
                  <span
                    onClick={() => {
                      this.trackGoToCheckoutEvent(cartContext);
                    }}
                  >
                    <PrimaryButtonLink
                      label={this.toCheckoutLabel}
                      isDisabled={!cartContext.products.length || cartContext.isLoading}
                      url={this.getCheckoutUrl(cartContext)}
                      className={styles.checkoutBtn}
                      isFullWidth
                    />
                  </span>
                  {this.showContinueShopping && (
                    <ContinueShoppingButton
                      continueShoppingLink={this.continueShoppingLink}
                      continueShoppingLabel={this.continueShoppingLabel}
                      context={this.context}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </CartContextConsumer>
      </div>
    );
  }
}

Cart.contextType = GlobalContext;
